import { useCallback, useEffect, useRef } from 'react';
import {EmbeddedBlock, EmbeddedBlockVariant} from '@tinct/core-api-models';
import { useShadowDom, useIframeBlockOverlays, useBlockVariantReplacer, useInlineTextEditor } from './hooks';
import { SavingOverlay } from './components/saving-overlay';

export type HtmlBlockSelectorProps = {
  html: string;
  blocks: EmbeddedBlock[];
  blockVariants?: EmbeddedBlockVariant[];
  selectedBlockId: string | null;
  onBlockSelect: (blockId: string | null) => void;
  /**
   * Makes the selected block's badge editable (AI personalization, CTA
   * tracking). Omit for read-only badges (previewer, demo, extension).
   */
  onBlockUpdate?: (block: EmbeddedBlock) => void;
  isLoading?: boolean;
  showAllLabels?: boolean;
  /** Called with the iframe's width (px) on mount and whenever it resizes. */
  onIframeResize?: (width: number) => void;
  /**
   * Canvas/artboard mode: render at exactly this CSS width instead of
   * filling the container. The iframe's own innerWidth drives breakpoint
   * selector resolution, so this IS the artboard's viewport width.
   */
  fixedWidth?: number;
  /**
   * Canvas/artboard mode: size the iframe to its full content height (no
   * internal scrolling — the surrounding canvas scrolls instead).
   */
  autoHeight?: boolean;
  /**
   * Artboard mode: the viewport height the document's `vh` units resolve
   * against. An auto-height iframe has no real viewport (it is as tall as its
   * content), so without this any viewport-sized section balloons.
   */
  viewportHeight?: number;
  /**
   * Flow mode: with `autoHeight`, let vertical scrolls chain out of the
   * iframe so the surrounding document scrolls (touch scrolling on mobile).
   * Off by default — the studio canvas forwards wheel events itself.
   */
  scrollChaining?: boolean;
  /**
   * `text` = inline text edition: clicking a default-rendered variant's
   * element edits it in place (see useInlineTextEditor); block selection is
   * suspended. Default `select` keeps today's behavior.
   */
  interactionMode?: 'select' | 'text';
  /** Persists an inline text edit; required for text mode to save. */
  onVariantEditCommit?: (blockVariantId: string, html: string) => Promise<string | null | undefined>;
};

export const HtmlBlockSelector = ({
  html,
  blocks,
  blockVariants,
  selectedBlockId,
  onBlockSelect,
  onBlockUpdate,
  isLoading = false,
  showAllLabels = false,
  onIframeResize,
  fixedWidth,
  autoHeight = false,
  viewportHeight,
  scrollChaining = false,
  interactionMode = 'select',
  onVariantEditCommit,
}: HtmlBlockSelectorProps) => {
  const { iframeRef, contentDocument, contentHeight } = useShadowDom({ html, viewportHeight, scrollChaining });

  // Report the iframe width on mount + every resize. The callback is kept in a
  // ref so the observer doesn't re-subscribe when the parent passes a new
  // function identity.
  const onIframeResizeRef = useRef(onIframeResize);
  useEffect(() => {
    onIframeResizeRef.current = onIframeResize;
  }, [onIframeResize]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[entries.length - 1]?.contentRect.width;
      if (width) onIframeResizeRef.current?.(width);
    });
    observer.observe(iframe);

    return () => observer.disconnect();
  }, [iframeRef]);

  const handleBlockSelect = useCallback(
    (blockId: string | null) => onBlockSelect(blockId),
    [onBlockSelect]
  );

  const blockVariantPortals = useBlockVariantReplacer({
    contentDocument,
    blockVariants: blockVariants ?? [],
    blocks,
  });

  const overlayPortals = useIframeBlockOverlays({
    contentDocument,
    blocks,
    selectedBlockId,
    onBlockSelect: handleBlockSelect,
    onBlockUpdate,
    blockVariants: blockVariants ?? [],
    showAllLabels,
    interactionMode,
  });

  const { savingElement } = useInlineTextEditor({
    contentDocument,
    enabled: interactionMode === 'text',
    blockVariants: blockVariants ?? [],
    onCommit: onVariantEditCommit,
  });

  const isArtboard = fixedWidth != null || autoHeight;

  return (
    <div
      className={
        isArtboard
          ? 'relative'
          : 'relative h-full w-full overflow-auto'
      }
      style={isArtboard ? { width: fixedWidth } : undefined}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      )}
      {/* Preview-iframe contract: exactly this sandbox (no top-navigation,
          forms, popups or modals), no referrer, eager load. */}
      <iframe
        ref={iframeRef}
        sandbox="allow-scripts allow-same-origin"
        referrerPolicy="no-referrer"
        loading="eager"
        className="block w-full border-none"
        style={{
          width: fixedWidth,
          // Before the content height is known, keep a viewport-ish height so
          // the artboard doesn't collapse to zero while loading.
          height: autoHeight ? Math.max(contentHeight, 800) : '100%',
        }}
        title="Landing page preview"
      />
      {blockVariantPortals}
      {overlayPortals}
      {savingElement && contentDocument && (
        <SavingOverlay target={savingElement} doc={contentDocument} />
      )}
    </div>
  );
};
